import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Answer } from '@/types/workerInfo.types';
import { colors, spacing } from '@/theme';
import { Check, ChevronDown } from 'lucide-react';

interface SingleChoiceInputProps {
  answer: Answer;
  options: string[];
  onBlur: (value: { textValue: string }) => void;
  disabled?: boolean;
}

export function SingleChoiceInput({ answer, options, onBlur, disabled }: SingleChoiceInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(answer.textValue || '');
  const [isSaved, setIsSaved] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleSelect = (option: string) => {
    if (disabled) return;
    
    setSelected(option);
    setIsOpen(false);
    
    if (option !== answer.textValue) {
      onBlur({ textValue: option });
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);
    }
  };

  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: spacing[2],
        }}
      >
        <div>
          <label
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: colors.gray[900],
              marginBottom: spacing[1],
            }}
          >
            {answer.question.question}
            {answer.question.isRequired && (
              <span style={{ color: colors.error?.[500] || '#ef4444', marginLeft: 4 }}>*</span>
            )}
          </label>
          {answer.question.description && (
            <p style={{ fontSize: 14, color: colors.gray[600], marginBottom: spacing[2] }}>
              {answer.question.description}
            </p>
          )}
        </div>

        <AnimatePresence>
          {isSaved && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing[1],
                color: colors.success?.[500] || '#10b981',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <Check size={16} />
              <span>Guardado</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: '100%',
          padding: spacing[4],
          borderRadius: 16,
          border: `2px solid ${
            isOpen 
              ? colors.primary[500] 
              : isHovered 
                ? colors.primary[300] 
                : colors.gray[200]
          }`,
          backgroundColor: isOpen ? colors.white : colors.gray[50],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span
          style={{
            color: selected ? colors.gray[900] : colors.gray[500],
            fontSize: 15,
          }}
        >
          {selected || 'Selecciona una opción...'}
        </span>
        <ChevronDown
          size={18}
          style={{
            color: colors.gray[400],
            transform: `rotate(${isOpen ? 180 : 0}deg)`,
            transition: 'transform 0.2s ease',
          }}
        />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: spacing[2],
              backgroundColor: colors.white,
              borderRadius: 16,
              border: `1px solid ${colors.gray[200]}`,
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              zIndex: 20,
              maxHeight: 300,
              overflowY: 'auto',
            }}
          >
            {options.map((option, index) => (
              <motion.button
                key={option}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => handleSelect(option)}
                style={{
                  width: '100%',
                  padding: spacing[3],
                  backgroundColor: selected === option ? colors.primary[50] : 'transparent',
                  border: 'none',
                  borderBottom: index < options.length - 1 ? `1px solid ${colors.gray[100]}` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primary[50];
                }}
                onMouseLeave={(e) => {
                  if (selected !== option) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <span
                  style={{
                    fontSize: 15,
                    color: selected === option ? colors.primary[700] : colors.gray[700],
                    fontWeight: selected === option ? 500 : 400,
                  }}
                >
                  {option}
                </span>
                {selected === option && (
                  <Check size={16} color={colors.primary[600]} />
                )}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}